import { Box, Button, CircularProgress, MobileStepper } from '@mui/material';
import { Option } from 'components/data-grid/data-grid-autocomplete';
import FlexBox from 'components/flex-box';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { isValidDecimal } from 'utils/validate-numbers';
import { getFeedersBrands, getFeeds, postFeeding } from 'views/production-systems/api/productionSystems';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import { FeedingProvider, useFeedingState } from 'views/production-systems/context/feeding-context';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { CreateFeedingInput } from 'views/production-systems/features/farm-overview/types/create-feeding-input.type';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { Feeding } from 'views/production-systems/types/feeding.type';
import { Pond } from 'views/production-systems/types/pond.type';
import { calculateBiomass } from 'views/production-systems/utils/farm-overview';
import AddFeedingGrid from './add-feeding-grid';
import ListSelectedPonds from '../list-selected-ponds';
import DuplicateRegistrationWarning, { REGISTRATION_EXISTS_QUERY_KEY } from '../duplicate-registration-warning';
import { RegistrationActionType } from '../../types/registration-action-type.enum';
import { PondLatestFeedingStatus } from 'views/production-systems/features/farm-overview/types/feeding-status.type';

type Props = {
  ponds: Pond[];
  handleClose?: () => void;
};

type Row = {
  pondId: string;
  pondName: string;
  zoneName: string;
  cycleId: number;
  cycleName: string;
  currentQty: number;
  currentMw: number;
  currentBm: number;
  amount: number;
  feed: { id: string; label: string };
  feeder: { id: string; label: string };
};

const AddFeeding: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const { errorDictionary, changeErrorDictionary, handleEditing, isEditing, hasError } = useDataGridError();
  const { feedingStatusQuery: { mutate: getLatestFeedingStatus, data: latestFeedingStatusData } } = useFeedingState();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [selectedPonds, setSelectedPonds] = useState<Pond[]>(ponds);

  //#region FETCH DATA

  const { data: feedData } = useGet(['getFeeds', account.company?.companyId], ({ queryKey }) => getFeeds(String(queryKey[1])));

  const { data: feederData } = useGet(['getFeeders', account.company?.companyId], ({ queryKey }) => getFeedersBrands(String(queryKey[1])));

  const feeds = useMemo<Option[]>(
    () => [...(feedData ?? [])].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map((f) => ({ id: String(f.id), label: f.name })),
    [feedData]
  );

  const feeders = useMemo<Option[]>(
    () => [...(feederData ?? [])].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map((f) => ({ id: String(f.id), label: f.name })),
    [feederData]
  );

  //#endregion

  //#region Data Grid

  const initialRows = useMemo(() => {
    return selectedPonds.map((pond) => {
      const pondStatus = latestFeedingStatusData?.pondLatestFeedingStatusData?.find((p) => p.pondId === pond.pondId);

      const feedValues = {
        feed: { id: String(pondStatus?.feedId ?? ''), label: pondStatus?.feedName ?? '', },
        feeder: { id: String(pondStatus?.feederId ?? ''), label: pondStatus?.feederName ?? '', },
      };

      return {
        pondId: pond.pondId,
        pondName: pond.pondName,
        zoneName: pond.zoneName,
        cycleId: pond.cycleId,
        cycleName: pond.cycleName,
        currentQty: pond.currentQty,
        currentMw: pond.currentMw,
        currentBm: calculateBiomass(pond.currentMw, pond.currentQty, true),
        amount: 0,
        ...feedValues,
      };
    });
  }, [selectedPonds, latestFeedingStatusData]);

  const [rows, setRows] = useState<Row[]>(initialRows || []);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  useEffect(() => {
    if (globalSiteId && selectedPonds.length > 0) {
      getLatestFeedingStatus({
        globalSiteId: globalSiteId,
        date: registrationDate.format('YYYY-MM-DD'),
        pondIds: selectedPonds.map((pond) => pond.pondId),
      });
    }
  }, [globalSiteId, registrationDate, selectedPonds]);

  //#region HANDLERS

  const handleSubmit = () => {
    let data = rows.map((row) => ({
      cycleId: row.cycleId,
      amount: row.amount,
      pondId: row.pondId,
      feedId: Number(row.feed!.id),
      feederId: Number(row.feeder!.id),
    }));

    createFeeding.mutate({
      globalSiteId: globalSiteId,
      data: {
        cycles: data,
        date: registrationDate.format('YYYY-MM-DD'),
      },
    });
  };

  //#endregion HANDLERS

  //#region POST DATA

  const createFeeding = usePost<Feeding, { globalSiteId: string; data: CreateFeedingInput }>(
    (request) => postFeeding(request.globalSiteId, request.data),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST DATA

  //#region HOOKS

  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return !isValidDecimal(row.amount) || Object.values(row.feed).every((x) => !x) || Object.values(row.feeder).every((x) => !x);
    });
  }, [rows]);

  //#endregion HOOKS

  return lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <FlexBox>
          <ListSelectedPonds selectedPonds={selectedPonds} setSelectedPonds={setSelectedPonds} />
        </FlexBox>
      )}
      {activeStep === 1 && (
        <>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.Feeding} ponds={selectedPonds} />
          <AddFeedingGrid
            rows={rows}
            feeds={feeds}
            feeders={feeders}
            setRows={setRows}
            handleEditing={handleEditing}
            errorDictionary={errorDictionary}
            changeErrorDictionary={changeErrorDictionary}
          />
        </>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              disabled={createFeeding.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              onClick={handleSubmit}
              startIcon={createFeeding.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={activeStep === 1}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

const FeedingContainer: React.FC<Props> = ({ ...props }) => {
  return (
    <FeedingProvider>
      <AddFeeding {...props} />
    </FeedingProvider>
  );
};

export default FeedingContainer;
